import Component from '@glimmer/component'
import { mapValues } from 'lodash'
import type { LessonItem } from 'teacher-ui-client/components/tables/skills-mastered/students-results'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import TabGroup from 'teacher-ui-client/components/tab-group'
import { eq } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import optional from 'teacher-ui-client/helpers/optional'
import fullGradeName from 'teacher-ui-client/helpers/full-grade-name'
import { t } from 'ember-intl'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'

interface Args {
  themeKey?: ThemeKeys
  lessonItemsByGrade?: Record<PropertyKey, LessonItem[]>
  gradePosition?: number
  setGradePosition?: (gradePosition: number) => void
}

interface LessonRange {
  start: number
  end: number
}

interface Signature {
  Args: Args
}

export class StudentsResultsGradeTabs extends Component<Signature> {
  readonly tabType = TAB_TYPE_KEYS.border

  /**
   * An array of available grade positions from the given lesson items. Determines which grade filter buttons to show.
   */
  get lessonGradePositions(): number[] {
    return Object.keys(this.args.lessonItemsByGrade ?? {}).map(Number)
  }

  /**
   * A mapping of grade position to lesson number ranges
   * {
   *   1: { start: 1, end: 50 },
   *   2: { start: 51, end: 100 },
   *   ...,
   * }
   */
  get gradeLessonRanges(): Record<number, LessonRange> {
    return mapValues(this.args.lessonItemsByGrade ?? {}, (lessonItems) => {
      const lessonNumbers = lessonItems.map((item) => item.lessonPosition)
      return { start: Math.min(...lessonNumbers), end: Math.max(...lessonNumbers) }
    })
  }

  lessonRangeFor = (gradePosition: number): LessonRange => {
    return this.gradeLessonRanges[gradePosition]
  }

  <template>
    <TabGroup @theme={{@themeKey}} @tabType={{this.tabType}} as |tabGroup|>
      {{#each this.lessonGradePositions as |gradePosition|}}
        {{#let (this.lessonRangeFor gradePosition) as |lessonRange|}}
          <tabGroup.tab
            data-test-grade-tab={{gradePosition}}
            class="text-sm leading-snug"
            @isActive={{eq @gradePosition gradePosition}}
            {{on "click" (fn (optional @setGradePosition) gradePosition)}}
          >
            {{fullGradeName gradePosition}}
            <div>
              {{t
                "components.reporting.tables.skillsMastered.studentsResults.gradeButtonLessons"
                start=lessonRange.start
                end=lessonRange.end
              }}
            </div>
          </tabGroup.tab>
        {{/let}}
      {{/each}}
    </TabGroup>
  </template>
}

export default StudentsResultsGradeTabs
